import { Injectable } from '@angular/core';
import { NsTrayPrintService } from './ns-tray-print.service';
import { MpsBatchForLabels, bagLabelJobs, bagPatientName } from './mps-label-builder';

/** Most physical bags one MPS bag can be split into; anything larger is almost certainly a typo. */
const MAX_PHYSICAL_BAGS = 20;

/**
 * Prints MPS bag labels (and their co-pay labels) through NS Tray. For each bag it asks how
 * many physical bags the pharmacist filled, then prints "1 of N" … "N of N". SafeScan doesn't
 * store N: a reprint simply asks again.
 */
@Injectable({ providedIn: 'root' })
export class MpsLabelPrinterService {
  constructor(private tray: NsTrayPrintService) {}

  /**
   * Prints the given bags, asking about every bag first. Resolves with the ids of the bags whose
   * labels all printed; a bag that was skipped or failed at the printer is left out.
   */
  async printBags(batch: MpsBatchForLabels, bagIds: string[]): Promise<string[]> {
    if (bagIds.length === 0) return [];
    // The screen may have only just connected (Finish on the issues page): wait for the printer list.
    await this.tray.whenReady();
    if (!this.tray.isConnected || !this.tray.selectedPrinter) {
      this.tray.alerts$.next({
        message: 'Cannot print bag labels — NS Tray not connected or no printer selected. Use Reprint bag labels once it is.',
        type: 'warning',
      });
      return [];
    }
    if (!this.tray.supportsBarcodes) {
      this.tray.alerts$.next({
        message: 'Bag labels not printed: this NS Tray is too old to print barcodes. Update NS Tray, then use Reprint bag labels.',
        type: 'warning',
      });
      return [];
    }

    const asked = bagIds
      .map(bagId => ({ bagId, count: this.askPhysicalBags(batch, bagId) }))
      .filter((b): b is { bagId: string; count: number } => b.count !== null);
    const results = await Promise.all(asked.map(async ({ bagId, count }) => {
      const jobs = bagLabelJobs(batch, bagId, count).map(lines => this.tray.print(lines));
      return jobs.length > 0 && (await Promise.all(jobs)).every(Boolean) ? bagId : null;
    }));
    return results.filter((id): id is string => id !== null);
  }

  private askPhysicalBags(batch: MpsBatchForLabels, bagId: string): number | null {
    const patient = bagPatientName(batch, bagId);
    const answer = window.prompt(`How many bags for ${patient} (bag ${bagId})?`, '1');
    if (answer === null) return null;

    const count = Number(answer.trim());
    if (!Number.isInteger(count) || count < 1 || count > MAX_PHYSICAL_BAGS) {
      this.tray.alerts$.next({
        message: `Bag ${bagId} not printed: enter a whole number of bags from 1 to ${MAX_PHYSICAL_BAGS}.`,
        type: 'warning',
      });
      return null;
    }
    return count;
  }
}
